import { Component, inject, OnInit, signal, } from '@angular/core';
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { ExportTypeEnum } from '@models/common/export-type.enum';
import { Company } from '@models/companies/company';
import { EntityChangeData } from '@models/documents/entity-change-data';
import { ColumnWithExportName } from "@models/grid/column-with-export-name";
import { GroupByOption } from "@models/grid/grid-filter";
import { AsicClassValidationError, SecuritySourcingResult } from "@models/requests/sourcing-result";
import { ClientSecurityTransaction } from "@models/security-transactions/client-security-transaction";
import { SecurityTransaction } from "@models/security-transactions/security-transaction";
import { SecurityTransactionType } from "@models/security-transactions/security-transaction-type.enum";
import {
  SecurityHoldingValidationError,
  SecurityHoldingValidationErrorMinusCurrentBalance,
  SecurityHoldingValidationErrorMinusHistoricalBalance
} from "@models/shares/security-holding-validation-error";
import { SubMenuItem } from "@models/ui-models/section-form-menu";
import {
  NgbDropdown,
  NgbDropdownItem,
  NgbDropdownMenu,
  NgbDropdownToggle,
  NgbModal,
} from '@ng-bootstrap/ng-bootstrap';
import {
  calculateSecurityTransactionsBalance
} from "@pages/shares/components/shares-page/utils/calculate-security-transactions-balance";
import { SharesExportService } from "@pages/shares/services/shares-export.service";
import { SharesGridService } from "@pages/shares/services/shares-grid.service";
import { CommonPageComponent } from "@shared/components/commons/common-page/common-page.component";
import { ConfirmModalComponent } from '@shared/components/commons/confirm/confirm-modal.component';
import { DividerComponent } from "@shared/components/commons/divider/divider.component";
import { DonutChartComponent } from "@shared/components/commons/donut-chart/donut-chart.component";
import {
  AgShareGroupComponent,
  IAgShareGroupCellRenderParams
} from "@shared/components/grid/ag-cells/shares/ag-share-group/ag-share-group.component";
import { BaseListGridComponent } from "@shared/components/grid/components/base-list-grid/base-list-grid.component";
import {
  GridClientSideComponent
} from "@shared/components/grid/components/grid-client-side/grid-client-side.component";
import { ButtonComponent } from "@shared/controls/button/button.component";
import { ToggleButtonComponent } from "@shared/controls/toggle-button/toggle-button.component";
import { HasRoleDirective } from "@shared/directives/has-role.directive";
import { ShareTypePipe } from "@shared/pipes/share-type.pipe";
import { CompaniesApiService } from "@shared/services/companies-api.service";
import { DocumentsApiService } from "@shared/services/documents-api.service";
import { IPartialHeaderData } from "@shared/services/header.service";
import { MenuService } from "@shared/services/menu.service";
import { PageSizeService } from "@shared/services/page-size.service";
import { SecurityApiService } from "@shared/services/security-api.service";
import { doNothing } from "@shared/utils/doNothing";
import { getPageTitle } from "@shared/utils/get-page-title";
import { getCompanyProfilePath, getGenerateDocumentPath } from "@shared/utils/route-path-utils";
import {
  ColDef,
  ColGroupDef,
  GetDetailRowDataParams,
  GetRowIdParams,
  GridApi,
  IDetailCellRendererParams,
  RowClassParams,
  RowClassRules,
  RowGroupingDisplayType,
} from "ag-grid-community";
import { ToastrService } from "ngx-toastr";
import { catchError, combineLatest, map, Observable, of, tap } from 'rxjs';
import { BaseShareFormComponent } from "../../../../forms/asic-forms/shares/base-share-form/base-share-form.component";
import {
  HistoricalTransactionComponent
} from "../../../../forms/asic-forms/shares/historical-transaction/historical-transaction.component";
import { CompanyDividendStatement, CompanyForm280, CompanyForm281, } from "../../../../forms/models/company";
import {
  CompanySecurityBalance,
  CompanySecurityCancellation,
  CompanySecurityConsolidationSubdivision,
  CompanySecurityIssue,
  CompanySecurityTransfer,
  HistoricalTransaction,
} from '../../../../forms/models/shares';
import { ModalFormsService } from '../../../../forms/services/modal-forms.service';
import { ShareGroupView } from "../../models/share-group-view";

interface IHolderClassValidationError {
  shareType: string;
  shareholderName: string;
  relationshipIds: string[];
  hashWithClass: string;
}

interface IShareValidationErrors {
  holdingCurrentMinusBalance: SecurityHoldingValidationErrorMinusCurrentBalance[];
  asicHolderRecordsValidationErrors: IHolderClassValidationError[];
  asicClassValidationErrors: AsicClassValidationError[];
  holdingHistoricalMinusBalance: SecurityHoldingValidationErrorMinusHistoricalBalance[];
}

@Component({
  selector: 'app-shares',
  standalone: true,
  imports: [
    HasRoleDirective,
    ButtonComponent,
    DividerComponent,
    NgbDropdown,
    NgbDropdownMenu,
    NgbDropdownToggle,
    NgbDropdownItem,
    DonutChartComponent,
    ToggleButtonComponent,
    GridClientSideComponent,
  ],
  providers: [SharesExportService],
  templateUrl: 'shares-page.component.html',
  styleUrl: 'shares-page.component.scss'
})
export class SharesPageComponent extends CommonPageComponent implements OnInit {
  private securityApiService = inject(SecurityApiService);
  private companiesApiService = inject(CompaniesApiService);
  private documentsApiService = inject(DocumentsApiService);

  private sharesGridService = inject(SharesGridService);
  private sharesExportService = inject(SharesExportService);
  private modalFormsService = inject(ModalFormsService);
  private modalService = inject(NgbModal);
  private toastr = inject(ToastrService);
  private menuService = inject(MenuService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private titleService = inject(Title);

  readonly pageTitle = 'Shares';
  override readonly headerData: IPartialHeaderData = {
    title: this.pageTitle,
    iconClass: 'ic-shares-20',
    backPath: [],
  };
  readonly addTransactionMenu: { name: string; action: () => void }[] = [
    { name: 'Issue', action: () => this.issueTransaction() },
    { name: 'Transfer', action: () => this.transferTransaction() },
    { name: 'Cancellation', action: () => this.cancelTransaction() },
    // { name: 'Subdivision or Consolidation', action: () => this.subdivisionOrConsolidationTransaction() },
    // { name: 'Conversion', action: () => this.conversionTransaction() },
    // { name: 'Change Amount Paid', action: () => this.changeAmountPaidTransaction() },
    { name: 'Add Historical Transaction', action: () => this.addHistoryTransaction() },
  ];
  readonly sharesFormMenu: SubMenuItem[] = [
    // {
    //   title: 'Notification of Share Buy-Back Details',
    //   formName: 'Form 280',
    //   action: () => this.openForm280(),
    // },
    // {
    //   title: 'Notification of Intention to Carry Out a Share Buy-Back',
    //   formName: 'Form 281',
    //   action: () => this.openForm281(),
    // },
    {
      title: 'Dividend Statement',
      formName: '',
      action: () => this.openDividendStatementForm(),
    },
  ];
  readonly shareGroupByOptions: GroupByOption[] = [
    { label: 'Shareholder', description: '', active: false },
    { label: 'Shareholder + Share Type', description: '', active: false },
    { label: 'Shareholder + Share Class', description: '', active: false },
    { label: 'Share Class', description: '', active: false },
  ];

  modalOpened = this.modalFormsService.modalOpened;
  isCompanyLoading = this.sharesGridService.isCompanyLoading.asReadonly();

  // Charts
  shareBreakdownTitle = 'Share Breakdown';
  shareBreakdownSeries: number[] = [];
  shareBreakdownLabels: string[] = [];

  shareTypeTitle = 'Share Type';
  shareTypeSeries: number[] = [];
  shareTypeLabels: string[] = [];

  // Grids State
  companyId = '';
  registry = new SecuritySourcingResult();
  groupedShareRows: ShareGroupView[] = [];
  checkedShares: ClientSecurityTransaction[] = [];
  securityTransactions: SecurityTransaction[] = [];
  clientSecurityTransaction: ClientSecurityTransaction[] = [];
  showUnlodgedChanges = true;
  sharesValidationErrors: IShareValidationErrors = {
    holdingCurrentMinusBalance: [],
    asicHolderRecordsValidationErrors: [],
    asicClassValidationErrors: [],
    holdingHistoricalMinusBalance: []
  };

  override ngOnInit(): void {
    super.ngOnInit();

    const companyId = this.route.snapshot.paramMap.get('id');

    if (companyId) {
      this.companyId = companyId;
      this.headerService.patchHeaderData({ backPath: getCompanyProfilePath(companyId) });
      this.menuService.updateMenuItem('Shares', { routerLink: ['shares', this.companyId] });

      this.loadCompanyProfile(companyId)
        .subscribe({
          next: (company) => {
            this.titleService.setTitle(getPageTitle(`Shares - ${ company.name }`));
          },
          error: (error) => {
            console.warn('[sharesPageComponent.loadCompanyProfile] error: ', error);
            this.titleService.setTitle(getPageTitle('Shares'));
          }
        });
    }

    this.sharesGridService.deleteTransaction.set(this.deleteTransaction.bind(this));
    this.sharesGridService.editTransaction.set(this.editTransaction.bind(this));

    this.menuService.setMenuState(true);
    this.loadTransactionsList();
  }

  // =====  Data preparation  =====

  onChangeShowUnlodgedChanges(showUnlodgedChanges: boolean): void {
    if (this.showUnlodgedChanges !== showUnlodgedChanges) {
      this.loadTransactionsList(showUnlodgedChanges);
    }

    this.showUnlodgedChanges = showUnlodgedChanges;
  }

  loadTransactionsList(showUnlodgedChanges = this.showUnlodgedChanges): void {
    this.isLoading.set(true);

    this.securityApiService.getSecurityRegistry(this.companyId, true, '', showUnlodgedChanges)
      .subscribe({
          next: (registry) => {
            this.registry = registry;

            if (registry.resultTransactions.length && registry.resultSecurityRecords.length) {
              registry.resultTransactions = calculateSecurityTransactionsBalance(registry.resultTransactions, false);

              this.securityTransactions = registry.resultTransactions;
              this.clientSecurityTransaction = registry.toClientSecurityTransaction(showUnlodgedChanges);

              this.fillShareBreakdownChartData();
              this.fillShareTypeChartData();

              this.sharesGridService.setSharesOptions(this.clientSecurityTransaction);
            } else {
              this.sharesGridService.setSharesOptions([]);
            }

            this.selectGroupByOption(this.currentGroupByOptionIndex);
            this.fillSharesValidationErrors(registry);
            this.isLoading.set(false);
          },
          error: (error) => {
            console.warn('[SharesComponent] error: ', error);
            this.toastr.error('Failed to load shares', 'Error');
            void this.router.navigate(['/companies']);
          }
        }
      );
  }

  setShareholderData(): void {
    const groupedShareRows = this.registry.toGroupByShareholders(this.clientSecurityTransaction);

    groupedShareRows.forEach((share) => {
      share.callRecords = calculateSecurityTransactionsBalance(share.callRecords);
      share.hasIssue = share.hasIssue || share.checkHasIssue();
      share.hasWarning = share.hasWarning || share.checkHasWarning();
      share.issueMessage = share.issueMessage || share.getIssueMessage();
    });

    this.groupedShareRows = this.calculateSumGroupedShareRows(groupedShareRows);
    this.groupedColDefs = this.sharesGridService.groupByShareholderColumnDefs;
  }

  setShareholderPlusShareTypeData(): void {
    const groupedShareRows = this.registry.toGroupByShareholderAndShareType(this.clientSecurityTransaction);
    groupedShareRows.forEach((share) => {
      share.callRecords = calculateSecurityTransactionsBalance(share.callRecords);
      share.hasIssue = share.hasIssue || share.checkHasIssue();
      share.hasWarning = share.hasWarning || share.checkHasWarning();
      share.issueMessage = share.issueMessage || share.getIssueMessage();
    });

    this.groupedShareRows = this.calculateSumGroupedShareRows(groupedShareRows);
    this.groupedColDefs = this.sharesGridService.groupByShareholderPlusShareTypeColumnDefs;
  }

  setShareholderPlusShareClassData(): void {
    const groupedShareRows = this.registry.toGroupByShareholderAndShareClass(this.clientSecurityTransaction, this.showUnlodgedChanges);
    groupedShareRows.forEach(share => {
      share.callRecords = calculateSecurityTransactionsBalance(share.callRecords);
      share.hasIssue = share.hasIssue || share.checkHasIssue();
      share.hasWarning = share.hasWarning || share.checkHasWarning();
      share.issueMessage = share.issueMessage || share.getIssueMessage();
    });

    this.groupedShareRows = this.calculateSumGroupedShareRows(groupedShareRows);
    this.groupedColDefs = this.sharesGridService.groupByShareholderPlusShareClassColumnDefs;
  }

  setShareClassData(): void {
    const groupedShareRows = this.clientSecurityTransaction.reduce((result: ShareGroupView[], share) => {
      const shareClass = share.securityType.class;
      const existingGroupIndex = result.findIndex((group) => group.shareClass === shareClass);
      const relatedAsicValidationError = this.sharesValidationErrors.asicClassValidationErrors.find((validationError) => validationError.class == shareClass);

      if (existingGroupIndex !== -1) {
        result[existingGroupIndex].callRecords.push(share);
      } else {
        const groupView = new ShareGroupView({
          shareClass,
          shareholderName: share.name,
          nonAsic: share.nonAsic,
          numberOfShares: 0,
          paidAmount: 0,
          unpaidAmount: 0,
          callRecords: [share],

          hasIssue: !this.showUnlodgedChanges && !!relatedAsicValidationError,
          hasWarning: false,
          issueMessage: 'The total aggregated figures of number of shares in the the registry does not match ASIC records. We recommend rectifying the register or using Form 492 to correct ASIC register if applicable ASAP',
          numberOfSharesAsic: relatedAsicValidationError?.asicTotalNumber,
          paidAmountAsic: relatedAsicValidationError?.asicTotalPaid,
          unpaidAmountAsic: relatedAsicValidationError?.asicTotalUnpaid,

          hash: share.hash,
          hashWithType: share.hashWithType,
          hashWithClass: share.hashWithClass,
        });

        result.push(groupView);
      }
      return result;
    }, []);

    groupedShareRows.forEach(share => {
      share.callRecords = calculateSecurityTransactionsBalance(share.callRecords);
    });

    this.groupedShareRows = this.calculateSumGroupedShareRows(groupedShareRows);
    this.groupedColDefs = this.sharesGridService.groupByShareClassColumnDefs;
  }

  fillShareBreakdownChartData(): void {
    const groupedShareRowsByShareholder = this.clientSecurityTransaction.reduce((result: ShareGroupView[], share) => {
      const shareholders = share.holders;
      const existingGroupIndex = result.findIndex(group => group.hash === share.hash);

      if (existingGroupIndex !== -1) {
        result[existingGroupIndex].callRecords.push(share);
      } else {
        const groupView = new ShareGroupView({
          shareholders,
          shareholderName: share.name,
          shareType: share.securityType.identifier,
          numberOfShares: 0,
          paidAmount: 0,
          unpaidAmount: 0,
          callRecords: [share],
          hash: share.hash,
          hashWithType: share.hashWithType,
          hashWithClass: share.hashWithClass,
        });

        result.push(groupView);
      }
      return result;
    }, []);

    const shareBreakdown: { series: number[], labels: string[] } = { series: [], labels: [] };
    const calculatedShareholder = this.calculateSumGroupedSharesForChart(groupedShareRowsByShareholder);
    const totalShares = calculatedShareholder.reduce((acc, item) => acc + item.numberOfShares, 0);
    calculatedShareholder.sort((a, b) => b.numberOfShares - a.numberOfShares);

    calculatedShareholder.forEach(shareholder => {
      const names = shareholder.shareholders?.map((holder) => holder.name).join(', ') ?? '';
      const percent = ((shareholder.numberOfShares / totalShares) * 100).toFixed(1);
      shareBreakdown.labels.push(names);
      shareBreakdown.series.push(Number(percent));
    });

    this.shareBreakdownLabels = shareBreakdown.labels;
    this.shareBreakdownSeries = shareBreakdown.series;
  }

  fillShareTypeChartData(): void {
    const groupedShareRowsByShareType = this.clientSecurityTransaction.reduce((result: ShareGroupView[], share) => {
      const shareholders = share.holders;
      const existingGroupIndex = result.findIndex((group) => group.hashWithType === share.hashWithType);

      if (existingGroupIndex !== -1) {
        result[existingGroupIndex].callRecords.push(share);
      } else {
        const groupView = new ShareGroupView({
          shareholders,
          shareholderName: share.name,
          shareType: share.securityType.identifier,
          numberOfShares: 0,
          paidAmount: 0,
          unpaidAmount: 0,
          callRecords: [share],
          hash: share.hash,
          hashWithType: share.hashWithType,
          hashWithClass: share.hashWithClass,
        });

        result.push(groupView);
      }
      return result;
    }, []);

    const shareType: { series: number[], labels: string[] } = { series: [], labels: [] };
    const calculatedShareType = this.calculateSumGroupedSharesForChart(groupedShareRowsByShareType);
    const totalShares = calculatedShareType.reduce((acc, item) => acc + item.numberOfShares, 0);
    calculatedShareType.sort((a, b) => b.numberOfShares - a.numberOfShares);

    calculatedShareType.forEach(shareholder => {
      const name = ShareTypePipe.formatValue(shareholder.shareType);
      const percent = ((shareholder.numberOfShares / totalShares) * 100).toFixed(1);
      shareType.labels.push(name);
      shareType.series.push(Number(percent));
    });

    this.shareTypeLabels = shareType.labels;
    this.shareTypeSeries = shareType.series;
  }

  calculateSumGroupedShareRows(groupedShareRows: ShareGroupView[]): ShareGroupView[] {
    groupedShareRows.forEach(shareholder => {
      shareholder.numberOfShares = shareholder.callRecords[0]?.numberBalance ?? 0;
      shareholder.paidAmount = shareholder.callRecords[0]?.paidBalance ?? 0;
      shareholder.unpaidAmount = shareholder.callRecords[0]?.unpaidBalance ?? 0;
    });

    return groupedShareRows
      .sort((rowA, rowB) => {
        const rowAName = (rowA.shareholderName ?? '')?.toLowerCase();
        const rowBName = (rowB.shareholderName ?? '')?.toLowerCase();

        if (rowAName === rowBName) return 0;

        return rowAName < rowBName
          ? -1
          : 1;
      });
  }

  calculateSumGroupedSharesForChart(groupedShareRows: ShareGroupView[]): ShareGroupView[] {
    groupedShareRows.forEach(shareholder => {
      shareholder.callRecords.forEach(transaction => {
        shareholder.numberOfShares += transaction.numberIncrease;
        shareholder.paidAmount += transaction.paidIncrease;
        shareholder.unpaidAmount += transaction.unpaidIncrease;
      });
    });

    return groupedShareRows;
  }

  loadCompanyProfile(companyId: string): Observable<Company> {
    return this.companiesApiService.getById(companyId);
  }

  // =====  Forms  =====

  issueTransaction(shareGroupView?: ShareGroupView): void {
    this.openWithCompanyProfile(new CompanySecurityIssue(), false, shareGroupView);
  }

  transferTransaction(shareGroupView?: ShareGroupView): void {
    this.openWithCompanyProfile(new CompanySecurityTransfer(), false, shareGroupView);
  }

  cancelTransaction(shareGroupView?: ShareGroupView): void {
    this.openWithCompanyProfile(new CompanySecurityCancellation(), false, shareGroupView);
  }

  subdivisionOrConsolidationTransaction(): void {
    this.openWithCompanyProfile(new CompanySecurityConsolidationSubdivision());
  }

  conversionTransaction(): void {
  }

  changeAmountPaidTransaction(): void {
  }

  addHistoryTransaction(shareGroupView?: ShareGroupView): void {
    this.openWithCompanyProfile(new HistoricalTransaction(), false, shareGroupView);
  }

  openForm280(): void {
    this.openWithCompanyProfile(new CompanyForm280());
  }

  openForm281(): void {
    this.openWithCompanyProfile(new CompanyForm281());
  }

  openDividendStatementForm(): void {
    this.openWithCompanyProfile(new CompanyDividendStatement());
  }

  openWithCompanyProfile(change: EntityChangeData, isEdit = false, shareGroupView?: ShareGroupView): void {
    if (this.modalOpened() || this.isCompanyLoading()) return;
    this.sharesGridService.isCompanyLoading.set(true);

    this.loadCompanyProfile(this.companyId)
      .subscribe((company) => {
        const props = { transactionsList: this.clientSecurityTransaction };
        const modalRef = this.modalFormsService.openModalWithCompany(change, company, isEdit, props);
        const instance = modalRef.componentInstance as BaseShareFormComponent<any, any>;

        if (shareGroupView) {
          instance.prefillData = shareGroupView;
          instance.shouldPrefillSecurityType = this.currentGroupByOptionIndex != null && this.shareGroupByOptions[this.currentGroupByOptionIndex].label === 'Shareholder + Share Type';
        }

        modalRef.result.then(
          () => this.loadTransactionsList(),
          doNothing
        );

        this.sharesGridService.isCompanyLoading.set(false);
      });
  }

  // =====  Grid Actions  =====

  editTransaction(transaction: SecurityTransaction): void {
    if (transaction.documentId && !transaction.isPosted) {
      void this.router.navigate(getGenerateDocumentPath(transaction.documentId));
    }

    this.editHistoricalTransaction(transaction);
  }

  editHistoricalTransaction(transaction: SecurityTransaction): void {
    if (!transaction.entityId || !transaction.securityTransactionId) {
      return;
    }

    combineLatest([
      this.getTransactionChange(transaction),
      this.companiesApiService.getById(transaction.entityId)
    ])
      .subscribe({
        next: ([change, company]) => {
          if (change) {
            this.openEditHistoricalTransactionModal(transaction, change, company);
          }
        },
        error: (error) => {
          console.warn('[loadHistoricalTransactionChange] error: ', error);
          this.toastr.error('Failed to load transaction.', 'Error');
        }
      });
  }

  openEditHistoricalTransactionModal(transaction: SecurityTransaction, change: EntityChangeData, company: Company): void {
    const isEdit = true;
    const securityTransactionChangeId = transaction.changeId;
    const modalRef = this.modalFormsService.openModal(HistoricalTransactionComponent);
    const instance = modalRef.componentInstance as HistoricalTransactionComponent;

    change.fillModalWithCompany(modalRef, company, isEdit);

    instance.currentSecurityTransactionIds = [transaction.securityTransactionId, transaction.pairTransactionId].filter((Boolean)) as string[];

    const isSubdivisionOrConsolidation = [
      SecurityTransactionType.ConsolidationFrom,
      SecurityTransactionType.ConsolidationTo,
      SecurityTransactionType.SubdivisionTo,
      SecurityTransactionType.SubdivisionFrom,
    ]
      .includes(transaction.transactionType);


    if (isSubdivisionOrConsolidation && securityTransactionChangeId) {
      const currentSecurityTransactionIds = new Set<string>();

      this.clientSecurityTransaction
        .filter((transaction) => transaction.changeId == securityTransactionChangeId)
        .forEach((transaction) => {
          if (transaction.securityTransactionId) {
            currentSecurityTransactionIds.add(transaction.securityTransactionId);
          }

          if (transaction.pairTransactionId) {
            currentSecurityTransactionIds.add(transaction.pairTransactionId);
          }
        });

      instance.currentSecurityTransactionIds = [...currentSecurityTransactionIds.values()];
    }


    modalRef.result
      .then(
        () => {
          this.loadTransactionsList();
        },
        doNothing
      );
  }

  deleteTransaction(transaction: SecurityTransaction): void {
    const modalRef = this.modalService.open(ConfirmModalComponent);
    const instance = modalRef.componentInstance as ConfirmModalComponent;
    let request: Observable<void>;

    if (!transaction.isPosted && transaction.documentId) {
      request = this.deleteUnlodgedTransaction(transaction.documentId);
      instance.message = `Deleting this transaction will also delete the related document. Are you sure you want to proceed?`;
    } else {
      request = this.deleteLodgedTransaction(transaction);
      instance.message = `Are you sure you would like to delete this security transaction?`;
    }

    instance.title = 'Delete transaction';
    instance.confirmText = 'Yes';
    instance.confirm = () => {
      return request
        .pipe(
          tap(() => {
            this.toastr.success('Security transaction deleted successfully.', 'Success');

            this.ngOnInit();
          }),
          catchError(() => {
            this.toastr.error('Error happened while deleting security transaction.', 'Error');
            return of(null);
          })
        );
    };
  }

  deleteUnlodgedTransaction(transactionDocumentId: string): Observable<void> {
    return this.documentsApiService.deleteDocument(transactionDocumentId);
  }

  deleteLodgedTransaction(transaction: SecurityTransaction): Observable<void> {
    return this.securityApiService.deleteSecurityTransaction(transaction.entityId!, transaction.securityTransactionId!);
  }

  // =====  Discrepancies  =====

  fillSharesValidationErrors(registry: SecuritySourcingResult = this.registry): void {
    // Reset current errors
    Object.keys(this.sharesValidationErrors).forEach((key) => this.sharesValidationErrors[key] = []);

    // Fill Minus Current and Historical Balance validation errors
    const securityHoldingValidationErrors = registry.getSecurityHoldingValidationErrors();
    securityHoldingValidationErrors.forEach((validationError) => {

      if (validationError.$type == SecurityHoldingValidationErrorMinusCurrentBalance.$type) {
        // Minus Current Balance
        this.sharesValidationErrors.holdingCurrentMinusBalance.push(validationError as SecurityHoldingValidationErrorMinusCurrentBalance);

      } else if (validationError.$type == SecurityHoldingValidationErrorMinusHistoricalBalance.$type) {
        // Minus Historical Balance
        this.sharesValidationErrors.holdingHistoricalMinusBalance.push(validationError as SecurityHoldingValidationErrorMinusHistoricalBalance);
      }
    });

    // Fill asic class validation errors
    this.sharesValidationErrors.asicClassValidationErrors = registry.asicClassValidationErrors;

    // Fill asic holder records validation errors
    this.registry.resultSecurityRecords
      .filter((record) => !record.nonAsic && record.validationError)
      .forEach((record) => {
        const holding = record.holdings[0] ?? null;
        const holdersNamesString = record.holders
          .map((holder) => holder.name)
          .join(', ');

        const error: IHolderClassValidationError = {
          shareholderName: holdersNamesString,
          shareType: holding?.securityType?.class ?? record.validationError?.class ?? '-',
          hashWithClass: holding?.hashWithClass ?? '',
          relationshipIds: record.holders.map((holder) => holder.relationshipId)
        };

        this.sharesValidationErrors.asicHolderRecordsValidationErrors.push(error);
      });
  }

  onShowSecurityHoldingValidationError(validationError: SecurityHoldingValidationError): void {
    const shareClassOptionIndex = this.shareGroupByOptions.findIndex((option) => option.label == 'Shareholder + Share Type');
    const isGroupBySharesClassViewActive = this.shareGroupByOptions[shareClassOptionIndex].active;

    if (!isGroupBySharesClassViewActive) {
      // Set Group by Shareholder and Share Type view
      this.selectGroupByOption(shareClassOptionIndex);
    }

    // Expand row with selected error
    setTimeout(() => {
      if (this.sharesGridService.gridApi.isDestroyed()) return;

      const validationErrorRelationshipIdsString = validationError.holdersRelationshipIds.join();
      const shareClassRowIndex = this.groupedShareRows.findIndex((row) => row.shareholders.map((holder) => holder.relationshipId).join() == validationErrorRelationshipIdsString);
      const shareClassRow = this.sharesGridService.gridApi.getDisplayedRowAtIndex(shareClassRowIndex);

      if (shareClassRow) {
        this.sharesGridService.gridApi.setRowNodeExpanded(shareClassRow, true, true);
      }
    }, 100);

    if (this.showUnlodgedChanges) {
      this.onChangeShowUnlodgedChanges(false);
    }
  }

  onShowClassMismatch(validationError: AsicClassValidationError): void {
    const shareClassOptionIndex = this.shareGroupByOptions.findIndex((option) => option.label == 'Share Class');
    const isGroupBySharesClassViewActive = this.shareGroupByOptions[shareClassOptionIndex].active;
    if (!isGroupBySharesClassViewActive) {
      // Set Group by Class view
      this.selectGroupByOption(shareClassOptionIndex);
    }

    // Expand row with selected error
    setTimeout(() => {
      if (this.sharesGridService.gridApi.isDestroyed()) return;

      const shareClassRowIndex = this.groupedShareRows.findIndex((row) => row.shareClass == validationError.class);
      const shareClassRow = this.sharesGridService.gridApi.getDisplayedRowAtIndex(shareClassRowIndex);

      if (shareClassRow) {
        this.sharesGridService.gridApi.setRowNodeExpanded(shareClassRow, true, true);
      }
    }, 100);

    if (this.showUnlodgedChanges) {
      this.onChangeShowUnlodgedChanges(false);
    }
  }

  onShowHolderClassMismatch(holderClassValidationError: IHolderClassValidationError) {
    const shareClassOptionIndex = this.shareGroupByOptions.findIndex((option) => option.label == 'Shareholder + Share Class');
    const isGroupBySharesClassViewActive = this.shareGroupByOptions[shareClassOptionIndex].active;
    if (!isGroupBySharesClassViewActive) {
      // Set Group by Class view
      this.selectGroupByOption(shareClassOptionIndex);
    }

    // Expand row with selected error
    setTimeout(() => {
      if (this.sharesGridService.gridApi.isDestroyed()) return;

      const relationshipIds = holderClassValidationError.relationshipIds;
      const shareClassRowIndex = holderClassValidationError.hashWithClass
        ? this.groupedShareRows.findIndex((row) => row.hashWithClass == holderClassValidationError.hashWithClass)
        : this.groupedShareRows.findIndex((row) => row.shareholders.every((holder) => relationshipIds.includes(holder.relationshipId)));

      const shareClassRow = this.sharesGridService.gridApi.getDisplayedRowAtIndex(shareClassRowIndex);

      if (shareClassRow) {
        this.sharesGridService.gridApi.setRowNodeExpanded(shareClassRow, true, true);
      }
    }, 100);

    if (this.showUnlodgedChanges) {
      this.onChangeShowUnlodgedChanges(false);
    }
  }

  get validationErrorsQuantity(): number {
    return this.sharesValidationErrors.holdingCurrentMinusBalance.length
      + this.sharesValidationErrors.asicClassValidationErrors.length
      + this.sharesValidationErrors.asicHolderRecordsValidationErrors.length
      + this.sharesValidationErrors.holdingHistoricalMinusBalance.length;
  }

  // =====  Grid  =====

  readonly defaultPageSize = PageSizeService.defaultPageSize;
  readonly detailRowAutoHeight = true;
  readonly rowSelection = BaseListGridComponent.noCheckboxesRowSelection;
  readonly groupDisplayType: RowGroupingDisplayType = "groupRows";
  readonly detailCellRenderer = AgShareGroupComponent;
  readonly rowClassRules: RowClassRules = {
    'non-lodged-data': (params: RowClassParams<ClientSecurityTransaction>) => !params.data?.isPosted,
  };
  readonly groupedRowClassRules: RowClassRules = {
    'transaction-alert': (params: RowClassParams<ShareGroupView>) => Boolean(params.data?.hasIssue),
    'transaction-warning': (params: RowClassParams<ShareGroupView>) => Boolean(params.data?.hasWarning),
  };

  colDefs = this.sharesGridService.colDefs;
  groupedColDefs = signal<(ColumnWithExportName | ColGroupDef)[]>([]);

  currentGroupByOptionIndex: number | null = null;
  detailCellRendererParams: unknown;

  onGridReady(gridApi: GridApi): void {
    this.sharesGridService.gridApi = gridApi;
    const autoSizeColumnList = this.sharesGridService.autoSizeColumnList;
    gridApi.autoSizeColumns(autoSizeColumnList);
    this.setExcelExportParams(gridApi);
  }

  selectShares(shares: ClientSecurityTransaction[]): void {
    this.checkedShares = shares;
  }

  selectGroupByOption(index: number | null): void {
    if (index == null) {
      this.clearGroupByOption();
      return;
    }

    if (!this.sharesGridService.gridApi.isDestroyed() && this.sharesGridService.gridApi.isAnyFilterPresent()) {
      this.sharesGridService.gridApi.setFilterModel(null);
    }

    this.currentGroupByOptionIndex = index;
    this.shareGroupByOptions.forEach((o, i) => o.active = i === index);

    if (this.shareGroupByOptions[index].label === 'Shareholder') {
      this.setShareholderData();
    } else if (this.shareGroupByOptions[index].label === 'Shareholder + Share Type') {
      this.setShareholderPlusShareTypeData();
    } else if (this.shareGroupByOptions[index].label === 'Shareholder + Share Class') {
      this.setShareholderPlusShareClassData();
    } else if (this.shareGroupByOptions[index].label === 'Share Class') {
      this.setShareClassData();
    }

    this.setDetailCellRendererParams();
  }

  clearGroupByOption(): void {
    this.shareGroupByOptions.forEach(o => o.active = false);
    const columnDef: ColDef | undefined = this.colDefs().find((col: ColDef) => col.field === 'transactionDate');

    if (columnDef) {
      columnDef.checkboxSelection = true;
      columnDef.headerCheckboxSelection = true;
    }

    this.groupedShareRows = [];
    this.currentGroupByOptionIndex = null;
    this.setExcelExportParams();
  }

  setDetailCellRendererParams(): void {
    const agShareGroupCellRenderParams: IAgShareGroupCellRenderParams = {
      showUnlodgedChanges: this.showUnlodgedChanges,
      exportShares: () => {
      },
      resolveIssue: () => {
      },
      issueTransaction: this.issueTransaction.bind(this),
      transferTransaction: this.transferTransaction.bind(this),
      cancelTransaction: this.cancelTransaction.bind(this),
      subdivisionOrConsolidationTransaction: this.subdivisionOrConsolidationTransaction.bind(this),
      conversionTransaction: this.conversionTransaction.bind(this),
      changeAmountPaidTransaction: this.changeAmountPaidTransaction.bind(this),
      addHistoryTransaction: this.addHistoryTransaction.bind(this),
      hasIssue: false,
      detailGridOptions: {
        columnDefs: this.colDefs() as unknown as ColGroupDef[],
        getRowId: (params: GetRowIdParams) => {
          return (params.data as { callId: string; }).callId;
        },
        suppressCellFocus: true,
        rowSelection: 'multiple',
      },
    };

    // remove detail group filters
    agShareGroupCellRenderParams.detailGridOptions.columnDefs = agShareGroupCellRenderParams.detailGridOptions.columnDefs!.map((colDef) => ({
      ...colDef,
      filterParams: { filterOptions: [] }
    }));

    this.detailCellRendererParams = {
      ...agShareGroupCellRenderParams,
      getDetailRowData: (params: GetDetailRowDataParams<unknown, unknown>) => {
        const data = (params.data as { callRecords: unknown; }).callRecords as unknown[];
        params.successCallback(data);
      },
      refreshStrategy: "rows",
    } as unknown as IDetailCellRendererParams<unknown, unknown>;
  }

  // =====  Export  =====

  readonly ExportTypeEnum = ExportTypeEnum;

  excelExportParams = this.sharesExportService.exportParamsXls();
  csvExportParams = this.sharesExportService.exportParamsCsv();
  excelStyles = this.sharesExportService.excelStyles;

  exportSelectedTransactionsToXls(): void {
    this.exportTransactionsList(true, ExportTypeEnum.EXCEL);
  }

  exportSelectedTransactionsToCsv(): void {
    this.exportTransactionsList(true, ExportTypeEnum.CSV);
  }

  exportTransactionsList(isBulkExport: boolean, exportType: ExportTypeEnum): void {
    if (this.modalOpened()) return;
    if (this.groupedShareRows.length) {
      this.groupExportToExcel();
      return;
    }

    this.sharesExportService.exportTransactionsList(
      this.sharesGridService.gridApi,
      this.colDefs(),
      this.checkedShares,
      this.clientSecurityTransaction,
      isBulkExport,
      exportType
    );
  }

  groupExportToExcel(): void {
    const a = true;
    if (a) {
      this.sharesExportService.groupExportToExcel(
        this.sharesGridService.gridApi,
        this.sharesGridService.colDefs(),
      );
      return;
    }
  }

  setExcelExportParams(gridApi: GridApi = this.sharesGridService.gridApi): void {
    if (this.groupedShareRows.length) {
      this.excelExportParams = this.sharesExportService.getGroupExcelExportParams(gridApi, this.colDefs());
    } else {
      this.excelExportParams = this.sharesExportService.exportParamsXls();
    }
  }

  getTransactionChange(transaction: SecurityTransaction): Observable<EntityChangeData> {
    return this.securityApiService.getTransactionChange(transaction.entityId!, transaction.securityTransactionId!)
      .pipe(
        map((change) => {
          switch (change.$type) {
            case CompanySecurityCancellation.$type:
              return new CompanySecurityCancellation(change as Partial<CompanySecurityCancellation>);
            case CompanySecurityIssue.$type:
              return new CompanySecurityIssue(change as Partial<CompanySecurityIssue>);
            case CompanySecurityTransfer.$type:
              return new CompanySecurityTransfer(change as Partial<CompanySecurityTransfer>);
            case CompanySecurityConsolidationSubdivision.$type:
              return new CompanySecurityConsolidationSubdivision(change as Partial<CompanySecurityConsolidationSubdivision>);
            case CompanySecurityBalance.$type:
              return new CompanySecurityBalance(change as Partial<CompanySecurityBalance>);
            default:
              throw new Error(`Unknown change data type: ${ change.$type }`);
          }
        })
      );
  }
}
